import { TransProvider } from '@mbarzda/solid-i18next'
import { Route, Navigate, useLocation } from '@solidjs/router'
import i18next from 'i18next'
import { Show, createEffect, type Component, type ParentProps } from 'solid-js'
import { SignedIn, SignedOut, useClerkLoadError, useUser as useClerkUser } from './shared/providers/clerk'
import './App.scss'
import translations from './assets/locales/translations.json'
import MainLayout from './components/layout/MainLayout'
import AccountSettings from './components/account-settings/AccountSettings'
import UsersOverview from './components/revenue/UsersOverview'
// import CashFlow from './components/revenue/CashFlow'
import ChurnOverview from './components/revenue/ChurnOverview'
// Engagement
import Sections from './components/engagement/Sections'
import Songs from './components/engagement/Songs'
import Videos from './components/engagement/Videos'
import Events from './components/engagement/Events'
import Community from './components/engagement/Community'
import Chat from './components/engagement/Chat'
import EngagementUsers from './components/engagement/EngagementUsers'
import UserDirectory from './components/user-directory/UserDirectory'
import Dashboard from './components/dashboard/Dashboard'
import Reports from './components/reports/Reports'
// Admin Panel
import ModerationPage from './features/moderation/ui/ModerationPage'
import KeywordFlaggingPage from './features/keyword-flagging/ui/KeywordFlaggingPage'
import KeywordFlaggingForm from './features/keyword-flagging/ui/KeywordFlaggingForm'
import PollsPage from './features/polls/ui/PollsPage'
import PollForm from './features/polls/ui/PollForm'
import AdminEventsPage from './features/events/ui/AdminEventsPage'
import AdminEventsCreatePage from './features/events/ui/AdminEventsCreatePage'
import AdminEventsEditPage from './features/events/ui/AdminEventsEditPage'
import AdminPresalePoolCreatePage from './features/events/ui/AdminPresalePoolCreatePage'
import AdminPresalePoolEditPage from './features/events/ui/AdminPresalePoolEditPage'
// Demographics
// import DemographicsUsers from './components/demographics/DemographicsUsers'
import { AuthProvider } from './shared/providers/auth-provider'
import { UserProvider, useUser } from './shared/providers/user-provider'
import { FeatureFlagsProvider } from './shared/feature-flags'
import { AppRoutes } from './shared/app-routes'
import { useNumberFormatter } from './shared/helpers/currency-formatter'
import { ErrorProvider } from './shared/providers/error.provider'
import { NotificationProvider } from './shared/notifications'
import { PeriodProvider } from './shared/providers/period.provider'
import { MRRSelectionProvider } from './shared/providers/mrr-selection.provider'
import { CorisRevenueProvider } from './shared/providers/coris/coris-revenue.provider'
import { CorisEngagementProvider } from './shared/providers/coris/coris-engagement.provider'
import { CorisDashboardProvider } from './shared/providers/coris/coris-dashboard.provider'
import { CorisSubscribersProvider } from './shared/providers/coris/coris-subscribers.provider'
import { CorisReportsProvider } from './shared/providers/coris/coris-reports.provider'
import { CorisModerationProvider } from './shared/providers/coris/coris-moderation.provider'
import { CorisKeywordFlaggingProvider } from './shared/providers/coris/coris-keyword-flagging.provider'
import { CorisPollsProvider } from './shared/providers/coris/coris-polls.provider'
import { CorisAdminEventsProvider } from './shared/providers/coris/coris-admin-events.provider'
import LoadingScreen from './components/LoadingScreen'
import ClerkLoadError from './components/ClerkLoadError'
import { User } from './features/auth/domain/models/user'
import { LoginRoute } from './routes/Login'

const AppProviders: Component<ParentProps> = (props) => {
  const clerkUser = useClerkUser()
  const { setCurrentUser } = useUser()
  const { currencyFormatter } = useNumberFormatter()

  createEffect(() => {
    currencyFormatter() // init locale
  })

  // Sync Clerk user to internal user context
  createEffect(() => {
    const user = clerkUser.user()
    if (user) {
      const internalUser = new User(
        user.id,
        user.primaryEmailAddress?.emailAddress ?? '',
        user.primaryEmailAddress?.verification?.status === 'verified',
        user.fullName ?? user.firstName ?? '',
        user.imageUrl ?? '',
        (user.publicMetadata?.customerId as string) ?? user.id
      )
      setCurrentUser(internalUser)
    }
  })

  createEffect(async () => {
    await i18next.init({
      lng: 'en',
      fallbackLng: 'en',
      interpolation: {
        escapeValue: true
      },
      resources: Object.fromEntries(Object.entries(translations).map(([lang, translation]) => [lang, { translation }]))
    })
  })

  return (
    <TransProvider>
      <ErrorProvider>
        <PeriodProvider>
          <MRRSelectionProvider>
            <CorisRevenueProvider>
              <CorisEngagementProvider>
                <CorisDashboardProvider>
                  <CorisSubscribersProvider>
                    <CorisReportsProvider>
                      <CorisModerationProvider>
                        <CorisKeywordFlaggingProvider>
                          <CorisPollsProvider>
                            <CorisAdminEventsProvider>
                              <NotificationProvider>
                                <MainLayout>{props.children}</MainLayout>
                              </NotificationProvider>
                            </CorisAdminEventsProvider>
                          </CorisPollsProvider>
                        </CorisKeywordFlaggingProvider>
                      </CorisModerationProvider>
                    </CorisReportsProvider>
                  </CorisSubscribersProvider>
                </CorisDashboardProvider>
              </CorisEngagementProvider>
            </CorisRevenueProvider>
          </MRRSelectionProvider>
        </PeriodProvider>
      </ErrorProvider>
    </TransProvider>
  )
}

// Routes component - exported for use in index.tsx
export const AppRoutesComponent: Component = () => {
  return (
    <>
      <Route path="/" component={() => <Navigate href={AppRoutes.Dashboard()} />} />
      <Route path={AppRoutes.Login()} component={LoginRoute} />
      <Route path={AppRoutes.AccountSettings()} component={AccountSettings} />
      {/* Demo pages  */}
      <Route path={AppRoutes.Dashboard()} component={Dashboard} />
      {/* Revenue */}
      <Route path={AppRoutes.UsersOverview()} component={UsersOverview} />
      {/* <Route path={AppRoutes.CashFlow()} component={CashFlow} /> */}
      <Route path={AppRoutes.ChurnOverview()} component={ChurnOverview} />
      {/* Engagement */}
      <Route path={AppRoutes.Sections()} component={Sections} />
      <Route path={AppRoutes.Songs()} component={Songs} />
      <Route path={AppRoutes.Videos()} component={Videos} />
      <Route path={AppRoutes.Events()} component={Events} />
      <Route path={AppRoutes.Community()} component={Community} />
      <Route path={AppRoutes.Chat()} component={Chat} />
      <Route path={AppRoutes.EngagementUsers()} component={EngagementUsers} />
      <Route path={AppRoutes.TopSongListeners()} component={EngagementUsers} />
      <Route path={AppRoutes.TopForumPosters()} component={EngagementUsers} />
      <Route path={AppRoutes.TopChatMessengers()} component={EngagementUsers} />
      {/* User Directory */}
      <Route path={AppRoutes.UserDirectory()} component={UserDirectory} />
      {/* Reports */}
      <Route path={AppRoutes.Reports()} component={Reports} />
      {/* Admin Panel */}
      <Route path={AppRoutes.Moderation()} component={ModerationPage} />
      <Route path={AppRoutes.KeywordFlagging()} component={KeywordFlaggingPage} />
      <Route path={AppRoutes.KeywordFlaggingNew()} component={KeywordFlaggingForm} />
      <Route path={AppRoutes.KeywordFlaggingEdit()}>
        <Route path=":id" component={KeywordFlaggingForm} />
      </Route>
      <Route path={AppRoutes.Polls()} component={PollsPage} />
      <Route path={AppRoutes.PollsNew()} component={PollForm} />
      <Route path={AppRoutes.PollsEdit()}>
        <Route path=":postId" component={PollForm} />
      </Route>
      <Route path={AppRoutes.AdminEvents()} component={AdminEventsPage} />
      <Route path={AppRoutes.AdminEventsCreate()} component={AdminEventsCreatePage} />
      <Route path={AppRoutes.AdminEventsEditBase()} component={AdminEventsEditPage} />
      <Route path={AppRoutes.AdminPresalePoolCreate()} component={AdminPresalePoolCreatePage} />
      <Route path={AppRoutes.AdminPresalePoolEdit()} component={AdminPresalePoolEditPage} />
      {/* Demographics
      <Route path={AppRoutes.DemographicsUsers()} component={DemographicsUsers} />
      */}
    </>
  )
}

// Root layout wrapper for the Router
export const AppRoot: Component<ParentProps> = (props) => {
  const clerkUser = useClerkUser()
  const clerkLoad = useClerkLoadError()
  const location = useLocation()
  const isLoginRoute = () => location.pathname === AppRoutes.Login()

  return (
    <>
      <Show when={!clerkUser.isLoaded()}>
        <Show when={clerkLoad.error()} fallback={<LoadingScreen />}>
          <ClerkLoadError />
        </Show>
      </Show>

      <Show when={clerkUser.isLoaded()}>
        <Show
          when={isLoginRoute()}
          fallback={
            <>
              <SignedIn>
                <AuthProvider>
                  <UserProvider>
                    <FeatureFlagsProvider>
                      <AppProviders>{props.children}</AppProviders>
                    </FeatureFlagsProvider>
                  </UserProvider>
                </AuthProvider>
              </SignedIn>

              <SignedOut>
                <Navigate href={AppRoutes.Login()} />
              </SignedOut>
            </>
          }
        >
          {props.children}
        </Show>
      </Show>
    </>
  )
}
